import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@apollo/client";
import { FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import { DateField } from "components/Forms/DateField";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import {
  FacilityPickerFragment,
  NotReportingReason,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { REPORTS_QUERY } from ".";
import { GET_REPORT } from "../Report/useReport";

/**
 * This component was originally created for the fulfillment and other report
 * tables.  This interface makes it a bit more generic in that we don't have a
 * specific type that is required. The data just needs to match the following
 * interface
 */
export interface NotReportingItem {
  reason?: NotReportingReason;
  facilityId: string;
  reportingYear: number;
  tenantId: string;
  tierIIReportId?: string | null;
  facilityDateClosed?: string | null;
  facilityDateInactive?: string | null;
  colocationReportingFacilityId?: string | null;
  isClosed?: boolean | null;
  isInactive?: boolean | null;
  isNotReporting?: boolean | null;
  isNotReportingThroughEncamp?: boolean | null;
}

interface NotReportingModalProps<T extends NotReportingItem> {
  reports: T[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback for successful submission (e.g., to clear selections)
}

interface FormInput {
  selectedOption?: NotReportingReason;
  dateClosed?: string;
  dateInactive?: string;
  coLocatedId?: string;
}

const COLOCATION_FACILITY = gql(`
  query CoLocatedFacility($id: ID!) {
    facility(id: $id) {
      ...FacilityPicker
    }
  }
`);

const BULK_UPDATE_NOT_REPORTING_REASON_MUTATION = gql(`
  mutation BulkUpdateNotReportingReason($input: NotReportingReasonInput!) {
    bulkUpdateNotReportingReason(input: $input) {
      succeeded
    }
  }
`);

export const getValidationSchema = (reportingYear: number) => {
  // For validation, we need to check that dates are before the start of the reporting year
  // since the facility was closed/inactive for the ENTIRE reporting year
  const yearStartDate = `${reportingYear}-01-01`; // Jan 1 of the reporting year as ISO string

  return yup.object().shape({
    selectedOption: yup.string().required("This field is required"),
    dateClosed: yup.string().when("selectedOption", {
      is: "CLOSED",
      then: yup
        .string()
        .required("This field is required")
        .test(
          "date-closed-valid",
          `Date closed must be before Jan 1, ${reportingYear} (since the facility was closed for the entire reporting year)`,
          function (value) {
            if (!value) return false;
            // Compare ISO date strings directly to avoid timezone issues
            return value < yearStartDate;
          }
        ),
    }),
    dateInactive: yup.string().when("selectedOption", {
      is: "INACTIVE",
      then: yup
        .string()
        .required("This field is required")
        .test(
          "date-inactive-valid",
          `Date inactive must be before Jan 1, ${reportingYear} (since the facility was inactive for the entire reporting year)`,
          function (value) {
            if (!value) return false;
            // Compare ISO date strings directly to avoid timezone issues
            return value < yearStartDate;
          }
        ),
    }),
    coLocatedId: yup.string().when("selectedOption", {
      is: "CO_LOCATED",
      then: yup.string().nullable().required("This field is required"),
    }),
  });
};

export function NotReportingModal<T extends NotReportingItem>(
  props: NotReportingModalProps<T>
) {
  const { open, onClose, reports, onSuccess } = props;
  const prevRefOpen = useRef(open);
  const [coLocatedFacility, setCoLocatedFacility] =
    useState<FacilityPickerFragment | null>(null);

  const singleMode = reports.length === 1;

  // All selected reports should have the same reporting year for bulk operations
  // Mixed reporting years are now prevented at the UI level by disabling bulk actions
  const reportingYear = useMemo(() => {
    const years = new Set(reports.map((report) => report.reportingYear));
    // Use the first reporting year (they should all be the same due to UI validation)
    return reports[0]?.reportingYear ?? currentTierIIReportingYear;
  }, [reports]);

  const reportingYearText = reportingYear.toString();

  const { loading } = useQuery(COLOCATION_FACILITY, {
    variables: { id: reports[0].colocationReportingFacilityId ?? "" },
    skip: !singleMode || !reports[0].colocationReportingFacilityId,
    onCompleted(data) {
      if (data.facility) {
        setCoLocatedFacility(data.facility);
      }
    },
  });

  const defaultValues = useMemo<FormInput>(() => {
    let defaultOption: NotReportingReason | undefined = undefined;
    if (singleMode) {
      const reportData = reports[0];
      if (reportData?.reason) {
        defaultOption = reportData.reason;
      } else {
        if (reportData?.isClosed) {
          defaultOption = NotReportingReason.Closed;
        } else if (reportData?.isInactive) {
          defaultOption = NotReportingReason.Inactive;
        } else if (reportData?.colocationReportingFacilityId) {
          defaultOption = NotReportingReason.CoLocated;
        } else if (reportData?.isNotReporting) {
          defaultOption = NotReportingReason.UnderThreshold;
        } else if (reportData?.isNotReportingThroughEncamp) {
          defaultOption = NotReportingReason.NotWithEncamp;
        } else {
          defaultOption = NotReportingReason.Closed;
        }
      }
    }
    return {
      selectedOption: defaultOption,
      dateClosed:
        singleMode && reports[0].facilityDateClosed
          ? reports[0].facilityDateClosed
          : undefined,
      dateInactive:
        singleMode && reports[0].facilityDateInactive
          ? reports[0].facilityDateInactive
          : undefined,
      coLocatedId:
        singleMode && reports[0]
          ? reports[0].colocationReportingFacilityId ?? undefined
          : "",
    };
  }, [reports, singleMode]);

  const validationSchema = useMemo(
    () => getValidationSchema(reportingYear),
    [reportingYear]
  );

  const { handleSubmit, control, watch, reset } = useForm<FormInput>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
    mode: "onChange", // Validate on every change
  });
  const selectedOption = watch("selectedOption");
  const [bulkUpdateNotReportingReason, { loading: saving }] = useMutation(
    BULK_UPDATE_NOT_REPORTING_REASON_MUTATION,
    {
      awaitRefetchQueries: true,
      onCompleted: () => {
        alerts.success(
          `Saved report${props.reports.length > 1 ? "s" : ""} successfully`
        );
      },
      onError: (error) => {
        alerts.error("An error occurred while saving the reports", error);
      },
      refetchQueries: [REPORTS_QUERY, GET_REPORT],
    }
  );
  const alerts = useAlerts();

  useEffect(() => {
    if (prevRefOpen.current === false && open === true) {
      reset(defaultValues);
    }
    prevRefOpen.current = open;
  }, [open, reset, defaultValues]);

  const onSubmit = useCallback(
    async (input: FormInput) => {
      if (input.selectedOption) {
        const result = await bulkUpdateNotReportingReason({
          variables: {
            input: {
              facilityIds: props.reports.map((report) => report.facilityId),
              reason: input.selectedOption,
              reportingYear: reportingYear,
              dateClosed: input.dateClosed,
              dateInactive: input.dateInactive,
              coLocatedId: input.coLocatedId,
            },
          },
        });
        if (!result.data?.bulkUpdateNotReportingReason?.succeeded) {
          alerts.error("Error saving reports.");
        } else {
          // Clear form state after successful submission
          reset();
          // Call success callback (e.g., to clear grid selections)
          onSuccess?.();
        }
        onClose();
      }
    },
    [
      bulkUpdateNotReportingReason,
      props.reports,
      alerts,
      onClose,
      onSuccess,
      reset,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title="Set Facility as Not Reporting"
      primaryActionButtonProps={{
        onClick: handleSubmit(onSubmit),
        loading: saving,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLabel component="legend">
          Select the reason this facility is not reporting:
        </FormLabel>
        <Controller
          name="selectedOption"
          control={control}
          defaultValue={selectedOption}
          rules={{ required: "This field is required" }}
          render={({ field }) => (
            <RadioGroup {...field}>
              <FormControlLabel
                value={NotReportingReason.Closed}
                control={<Radio />}
                label={`This facility was closed for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
              />
              {selectedOption === NotReportingReason.Closed && (
                <DateField
                  name="dateClosed"
                  label="Date Closed"
                  control={control}
                />
              )}

              <FormControlLabel
                value={NotReportingReason.Inactive}
                control={<Radio />}
                label={`This facility was inactive for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
              />
              {selectedOption === NotReportingReason.Inactive && (
                <DateField
                  name="dateInactive"
                  label="Date Inactive"
                  control={control}
                />
              )}

              <FormControlLabel
                value={NotReportingReason.CoLocated}
                control={<Radio />}
                label="This facility is co-located on the same site with another facility"
              />
              {selectedOption === NotReportingReason.CoLocated && (
                <Controller
                  name="coLocatedId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FacilityPicker
                      {...field}
                      {...fieldState}
                      disabled={!!defaultValues.coLocatedId && loading}
                      value={coLocatedFacility}
                      defaultSearchTerm={`tenantId:${reports[0].tenantId}`}
                      label="Co-located Facility"
                      disabledFacilitiesById={props.reports.map(
                        (report) => report.facilityId
                      )}
                      onChange={(fac) => {
                        setCoLocatedFacility(fac);
                        field.onChange(fac?.id ?? null);
                      }}
                    />
                  )}
                />
              )}

              <FormControlLabel
                value={NotReportingReason.UnderThreshold}
                control={<Radio />}
                label={`This facility's products were under the threshold for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
              />

              <FormControlLabel
                value={NotReportingReason.NotWithEncamp}
                control={<Radio />}
                label="This facility is not reporting through Encamp"
              />
            </RadioGroup>
          )}
        />
      </form>
    </Dialog>
  );
}
